const IPV4_RE = /^\d{1,3}(\.\d{1,3}){3}$/;

const normalize = (domain?: string | null): string =>
  (domain || "")
    .trim()
    .toLowerCase()
    .replace(/^\./, "");

/**
 * Resolves the cookie `Domain` attribute for first-party cookies.
 *
 * The marketing site (life5.xx) and the app (app.life5.xx) must share the
 * attribution and identity cookies, so they have to be written on the
 * registrable domain. `REACT_APP_COOKIE_DOMAIN` is honoured when it matches
 * the current host; otherwise the domain is derived from the hostname so a
 * missing/wrong env var can never produce `Domain=undefined` (cookie rejected
 * by the browser) or a host-only cookie that shadows the shared one.
 *
 * Returns `undefined` (host-only cookie) on localhost / IP hosts.
 */
export const resolveCookieDomain = (
  hostname: string | undefined | null,
  envDomain?: string | null
): string | undefined => {
  const host = (hostname || "").trim().toLowerCase();
  if (!host || host === "localhost" || host.endsWith(".localhost") || IPV4_RE.test(host)) {
    return undefined;
  }

  const env = normalize(envDomain);
  if (env && env !== "undefined" && env !== "null" && (host === env || host.endsWith(`.${env}`))) {
    return env;
  }

  const labels = host.split(".").filter(Boolean);
  if (labels.length < 2) return undefined;
  return labels.slice(-2).join(".");
};

export const getCookieDomain = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  return resolveCookieDomain(window.location.hostname, process.env.REACT_APP_COOKIE_DOMAIN);
};

/**
 * True when `hostname` belongs to the same registrable domain as the app
 * (life5.es, life5.it, ...). Used to tell an arrival from our own marketing
 * site apart from a genuine external landing.
 */
export const isOwnHost = (hostname: string | undefined | null, cookieDomain?: string): boolean => {
  const host = (hostname || "").trim().toLowerCase();
  if (!host) return false;
  const domain = cookieDomain ?? getCookieDomain();
  if (!domain) {
    return typeof window !== "undefined" && host === window.location.hostname.toLowerCase();
  }
  return host === domain || host.endsWith(`.${domain}`);
};
